// Extrait de ClinicAdminDashboard.js (handover bloc F3-A) — navigation croisée via callbacks de la page.
import { BarChart3, UserPlus, Users, ChevronRight, TrendingUp, Building2 } from "lucide-react";
import { ROLES } from "@/components/admin/modals/ClinicAdminModals";

function StatTile({ value, label, testId, onClick, accent = "text-slate-900" }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      data-testid={testId}
      className={`p-4 bg-slate-50/80 border border-slate-200/60 rounded-xl text-center transition-colors ${onClick ? "hover:bg-emerald-50 hover:border-emerald-200 group" : ""}`}
    >
      <p className={`text-2xl font-bold font-display tracking-tight ${accent} group-hover:text-emerald-800`}>{value}</p>
      <p className="mt-1 text-xs font-medium text-slate-500 group-hover:text-emerald-700 leading-tight">{label}</p>
    </Tag>
  );
}

export default function ClinicOverviewTab({ kpi, users, onOpenCreatePatient, onOpenCreateUser, onGoToPerformance, onGoToTeamRole }) {
  const teamRoles = ROLES.filter(r => r.value !== "ADMIN_CLINIQUE");
  const actions = [
    { label: "Nouveau patient", icon: UserPlus, onClick: onOpenCreatePatient, testId: "quick-add-patient" },
    { label: "Nouvel utilisateur", icon: Users, onClick: onOpenCreateUser, testId: "quick-add-user" },
    { label: "Voir la performance", icon: TrendingUp, onClick: onGoToPerformance, testId: "quick-analytics" },
  ];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
      <div className="lg:col-span-8 flex flex-col gap-6">
        {/* Performance */}
        <div className="dash-card">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Performance
          </h3>
          <div className="grid grid-cols-3 gap-4">
            <StatTile value={kpi?.checkins?.dailyRate ?? "—"} label="Check-ins / jour" accent="text-primary" testId="perf-checkins-daily" />
            <StatTile value={kpi?.alerts?.avgResponseMinutes ? `${kpi.alerts.avgResponseMinutes} min` : "—"} label="Temps de réponse" testId="perf-response-time" />
            <StatTile value={kpi?.staff?.patientsPerIde ?? "—"} label="Patients / IDE" testId="perf-patients-per-ide" />
          </div>
        </div>

        {/* Team Summary */}
        <div className="dash-card flex-1">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-slate-600" />
            Équipe médicale
          </h3>
          <div className={`grid grid-cols-2 gap-4 w-full ${teamRoles.length >= 4 ? "sm:grid-cols-4" : "sm:grid-cols-3"}`} data-testid="team-summary-grid">
            {teamRoles.map(role => (
              <StatTile
                key={role.value}
                value={users.filter(u => u.role === role.value && u.isActive !== false).length}
                label={role.label}
                onClick={() => onGoToTeamRole(role.value)}
                testId={`team-count-${role.value}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="lg:col-span-4">
        <div className="dash-card h-full flex flex-col">
          <h3 className="font-semibold text-slate-800 mb-4">Actions rapides</h3>
          <div className="space-y-2">
            {actions.map(({ label, icon: Icon, onClick, testId }) => (
              <button
                key={testId}
                onClick={onClick}
                data-testid={testId}
                className="w-full flex items-center gap-3 p-3 bg-slate-50 hover:bg-emerald-50 border border-slate-200/60 hover:border-emerald-200 text-slate-700 hover:text-emerald-800 rounded-xl transition-colors"
              >
                <Icon className="w-5 h-5 text-primary" />
                <span className="font-medium text-sm">{label}</span>
                <ChevronRight className="w-4 h-4 ml-auto text-slate-400" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
